// src/contexts/administrativo/ConfigModuloContext.js
// contexts/administrativo/ConfigModuloContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchData, postData, putData, deleteData } from '../../services/Api';
import { normalizarDFePadrao } from '../../models/administrativo/ConfigModulo.js';
import { useAlert } from '../geral/AlertContext';

const ConfigModuloContext = createContext();
export const useConfigModulo = () => useContext(ConfigModuloContext);

export const ConfigModuloProvider = ({ children }) => {
  const [configModuloList, setConfigModuloList] = useState([]);
  const [configModuloSel, setConfigModuloSel] = useState(null);
  const [dFeSelecionadoSessao, setDFeSelecionadoSessao] = useState(null);
  const [versao, setVersao] = useState(null);

  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);

  const { showAlert } = useAlert();

  const fetchConfigModulos = useCallback(async () => {
    const result = await fetchData('/ConfigModulo');
    const configSelecionada = result?.length > 0 ? result[0] : null;
    setConfigModuloList(result || []);
    setConfigModuloSel(configSelecionada);
    setDFeSelecionadoSessao((selecaoAtual) => (
      selecaoAtual ?? normalizarDFePadrao(configSelecionada?.dFePadrao)
    ));
    return result;
  }, []);

  const fetchVersaoBanco = useCallback(async () => {
    const result = await fetchData('/ConfigModulo/GetVersao');
    setVersao(result);
    return result;
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadAll = async () => {
      setLoading(true);
      setReady(false);

      try {
        const [modulos, v] = await Promise.all([fetchConfigModulos(), fetchVersaoBanco()]);

        // ✅ só fica "ready" se realmente carregou algo
        const ok = Array.isArray(modulos) && v !== null && v !== undefined;

        if (!cancelled) setReady(ok);

        if (!ok) {
          showAlert('Falha ao carregar configurações do sistema!', 'danger');
        }
      } catch (error) {
        console.error("Erro ao buscar configurações de módulos:", error);
        showAlert('Erro ao buscar configurações de módulos!', 'danger');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadAll();
    return () => { cancelled = true; };
  }, [fetchConfigModulos, fetchVersaoBanco, showAlert]);

  const addConfigModulo = async (configModulo) => {
    try {
      const result = await postData('/ConfigModulo', configModulo);
      setConfigModuloList((prev) => [...prev, result]);
      showAlert('Configuração de módulo adicionada com sucesso!');
    } catch (error) {
      console.error("Erro ao adicionar configuração de módulo:", error);
      showAlert('Erro ao adicionar configuração de módulo!', 'danger');
    }
  };

  const updateConfigModulo = async (configModulo) => {
    try {
      const result = await putData('/ConfigModulo', configModulo);
      setConfigModuloList((prev) => prev.map((c) => (c.id === result.id ? result : c)));
      setConfigModuloSel((prev) => (prev?.id === result.id ? result : prev));
      showAlert('Configuração de módulo alterada com sucesso!');
    } catch (error) {
      console.error("Erro ao atualizar configuração de módulo:", error);
      showAlert('Erro ao alterar configuração de módulo!', 'danger');
    }
  };

  const deleteConfigModulo = async (id) => {
    try {
      await deleteData('/ConfigModulo', { configModuloId: id });
      setConfigModuloList((prev) => prev.filter((c) => c.id !== id));
      showAlert('Configuração de módulo excluída com sucesso!');
    } catch (error) {
      console.error("Erro ao excluir configuração de módulo:", error);
      showAlert('Erro ao excluir configuração de módulo!', 'danger');
    }
  };

  return (
    <ConfigModuloContext.Provider
      value={{
        configModuloList,
        configModuloSel,
        dFeSelecionadoSessao,
        setDFeSelecionadoSessao,
        versao,
        loading,
        ready,
        addConfigModulo,
        updateConfigModulo,
        deleteConfigModulo
      }}
    >
      {children}
    </ConfigModuloContext.Provider>
  );
};
